import React, { forwardRef, useEffect, useState } from "react";

import Modal from "react-modal";

import { usePrefersReducedMotion } from "simplydo/react/ui/utils";
import { getDefaultMountNode } from "simplydo/react/ui/utils/mountNode";

import "./sliding-pane.css";
import type { SlidingPaneProps } from "./types";

const SlidingPane = forwardRef(
  (props: SlidingPaneProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      isOpen,
      onOpen,
      onClose,
      onAfterClose,
      children,
      style,
      from = "right",
      width,
      duration: preferredDuration = 150,
      zIndex = 900,
    } = props;
    const [modalOpen, setModalOpen] = useState(isOpen);
    const duration = usePrefersReducedMotion() ? 0 : preferredDuration;

    useEffect(() => {
      setModalOpen(isOpen);
    }, [isOpen]);

    return (
      <Modal
        parentSelector={() => getDefaultMountNode() ?? document.body}
        ariaHideApp={false}
        className={`sliding-pane sliding-pane_from_${from}`}
        style={{
          content: {
            width: width || "80%",
            maxWidth: "100%",
            transition: `transform ${duration / 1000}s ease-out`,
            display: "flex",
            ...style,
          },
          overlay: {
            transition: `background-color ${duration / 1000}s ease-out`,
            zIndex: zIndex,
          },
        }}
        overlayClassName="sliding-pane__overlay"
        closeTimeoutMS={duration}
        isOpen={modalOpen}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onAfterOpen={onOpen}
        onClose={onClose}
        onAfterClose={onAfterClose}
        bodyOpenClassName="sliding-pane__body-open"
        onRequestClose={onClose}
      >
        <div
          style={{
            maxHeight: "100vh",
            overflow: "auto",
            display: "flex",
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
          ref={ref}
          onKeyDown={(e) => {
            /* Prevent react-modal scopeTab from swallowing Tab (broken tabbable
            detection with this pane's overflow/flex layout). Body-trap still
            keeps focus inside the modal at the edges. */
            if (e.key === "Tab") {
              e.stopPropagation();
            }
          }}
        >
          {children}
        </div>
      </Modal>
    );
  },
);

SlidingPane.displayName = "SlidingPane";

export default SlidingPane;
